/**
 * RESPONSIBILITY: translate between the stored repeat text and what the picker holds.
 *
 * The picker needs to hold choices the stored text cannot: "the 3rd …" before a day has
 * been picked, or "on a date" before the date has. Those are a person part-way through,
 * and dropping them on every change would undo the choice they had just made. So the
 * picker keeps a draft, and only the draft's complete parts are written — see core
 * repeat-rule.ts for why half a rule is still a legal value.
 */
import {
  formatRepeat, readRepeat, type Ordinal, type RepeatFreq, type RepeatRule,
} from '@framework/core';

export interface RepeatDraft {
  /** '' is "does not repeat", and stores nothing. */
  freq: '' | RepeatFreq;
  /** Monthly only: a counted day of the week, or a date. */
  monthly: 'weekday' | 'date';
  days: number[];
  nth?: Ordinal;
  monthDay?: number;
  month?: number;
  /** HH:MM as <input type=time> gives it, or '' for no time of day. */
  time: string;
}

export const EMPTY_DRAFT: RepeatDraft = { freq: '', monthly: 'weekday', days: [], time: '' };

const pad = (n: number): string => String(n).padStart(2, '0');

export function draftOf(text: string): RepeatDraft {
  const r = readRepeat(text);
  if (!r) return EMPTY_DRAFT;
  return {
    freq: r.freq,
    monthly: r.freq === 'monthly' && r.nth === undefined && r.monthDay !== undefined ? 'date' : 'weekday',
    days: r.days, nth: r.nth, monthDay: r.monthDay, month: r.month,
    time: r.hour !== undefined && r.minute !== undefined ? `${pad(r.hour)}:${pad(r.minute)}` : '',
  };
}

/**
 * A new frequency starts from nothing but the time of day. Carrying the rest across is
 * how "Tue and Thu, weekly" turned into a monthly rule already showing "Tuesday" that
 * nobody had chosen for it, with Thursday kept out of sight.
 */
export const withFreq = (d: RepeatDraft, freq: RepeatDraft['freq']): RepeatDraft =>
  ({ ...EMPTY_DRAFT, freq, time: freq ? d.time : '' });

/** How many days the chosen month can have — 29 for February, which a leap year has. */
export const daysInMonth = (month: number | undefined): number =>
  month ? new Date(2000, month, 0).getDate() : 31;

export function textOf(d: RepeatDraft): string {
  if (!d.freq) return '';
  const rule: RepeatRule = { freq: d.freq, days: [] };

  if (d.freq === 'weekly') rule.days = d.days;
  if (d.freq === 'monthly' && d.monthly === 'weekday' && d.nth !== undefined && d.days.length) {
    rule.nth = d.nth;
    rule.days = d.days.slice(0, 1);
  }
  if (d.freq === 'monthly' && d.monthly === 'date') rule.monthDay = d.monthDay;
  if (d.freq === 'yearly') { rule.month = d.month; rule.monthDay = d.monthDay; }

  const m = /^(\d{1,2}):(\d{2})/.exec(d.time);
  if (m) { rule.hour = Number(m[1]); rule.minute = Number(m[2]); }
  return formatRepeat(rule);
}
